import { useState } from 'react';
import { Layers, Plus, Library, Search, Wrench } from 'lucide-react';
import type { CreateFilamentInput, CreateSpoolInput } from '@maker-toolchest/shared';
import { SplitButton } from '@/components/SplitButton';
import { EmptyState } from '@/components/EmptyState';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/Tabs';
import {
  FilamentLibraryBrowser,
  MyFilamentsList,
  FilamentForm,
  useCreateFilament,
  useTenantFilaments,
  useAddFilamentToLibrary,
} from '@/features/filaments';
import { SpoolForm, useCreateSpool } from '@/features/spools';

export function FilamentsPage() {
  const [activeTab, setActiveTab] = useState('my-library');
  const [formOpen, setFormOpen] = useState(false);
  const [spoolFormOpen, setSpoolFormOpen] = useState(false);
  const [preselectedFilamentId, setPreselectedFilamentId] = useState<string>('');

  const { data, isLoading } = useTenantFilaments();
  const createMutation = useCreateFilament();
  const addToLibraryMutation = useAddFilamentToLibrary();
  const createSpoolMutation = useCreateSpool();

  const filaments = data?.items || [];
  const hasFilaments = filaments.length > 0;

  const handleCreateFilament = async (formData: CreateFilamentInput) => {
    try {
      // Create the custom filament in the library
      const filament = await createMutation.mutateAsync(formData);
      // Auto-add it to tenant's library
      await addToLibraryMutation.mutateAsync({ filamentId: filament.id });
      setFormOpen(false);
    } catch {
      // Error handled by mutation
    }
  };

  const handleAddSpools = (tenantFilamentId: string) => {
    setPreselectedFilamentId(tenantFilamentId);
    setSpoolFormOpen(true);
  };

  const handleCreateSpool = async (formData: CreateSpoolInput) => {
    try {
      await createSpoolMutation.mutateAsync(formData);
      setSpoolFormOpen(false);
      setPreselectedFilamentId('');
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Page header - Full width */}
      <div className="flex-shrink-0 p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-foreground">Filaments</h1>
            <p className="text-sm text-foreground-muted">
              Manage your filament library and browse available filaments
            </p>
          </div>
          <div className="flex items-center gap-2">
            <SplitButton
              label="Add Filament"
              icon={<Plus className="h-4 w-4" />}
              items={[
                {
                  label: 'From Library',
                  icon: <Library className="h-4 w-4" />,
                  onClick: () => setActiveTab('browse'),
                },
                {
                  label: 'Custom Filament',
                  icon: <Wrench className="h-4 w-4" />,
                  onClick: () => setFormOpen(true),
                },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 overflow-y-auto p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="my-library">
              <Library className="mr-2 h-4 w-4" />
              My Library ({filaments.length})
            </TabsTrigger>
            <TabsTrigger value="browse">
              <Search className="mr-2 h-4 w-4" />
              Browse All
            </TabsTrigger>
          </TabsList>

          <TabsContent value="my-library" className="mt-4">
            {isLoading && !hasFilaments ? (
              <div className="rounded border border-border bg-background-subtle p-12 text-center">
                <p className="text-foreground-muted">Loading filaments...</p>
              </div>
            ) : !hasFilaments ? (
              <div className="rounded border border-border bg-background-subtle p-4">
                <EmptyState
                  icon={<Layers className="h-10 w-10" />}
                  title="No filaments in your library"
                  description="Add filaments from the library or create custom ones to start tracking your inventory."
                  actionLabel="Browse Library"
                  onAction={() => setActiveTab('browse')}
                />
              </div>
            ) : (
              <MyFilamentsList onAddSpools={handleAddSpools} />
            )}
          </TabsContent>

          <TabsContent value="browse" className="mt-4">
            <FilamentLibraryBrowser />
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Custom Filament Form */}
      <FilamentForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleCreateFilament}
        loading={createMutation.isPending || addToLibraryMutation.isPending}
      />

      {/* Add Spool Form */}
      <SpoolForm
        open={spoolFormOpen}
        onClose={() => {
          setSpoolFormOpen(false);
          setPreselectedFilamentId('');
        }}
        onSubmit={handleCreateSpool}
        preselectedFilamentId={preselectedFilamentId}
        loading={createSpoolMutation.isPending}
      />
    </div>
  );
}
